// パフォーマー認証状態の管理
// localStorage に access/refresh トークンとパフォーマー情報を保存。
// API 呼び出しは src/api/client.ts 経由で Authorization ヘッダが自動付与される。
//
// セッション維持 / 期限切れ対応:
// - 画面を開いている間は access token の `exp` を読んで残り 2 分の時点で
//   silent refresh を仕込む(heartbeat)。
// - ブラウザがバックグラウンドで setTimeout がスロットルされても、
//   visibilitychange で foreground に戻った瞬間に再評価する。
// - apiRequest が refresh にも失敗したら `auth-events` から通知が来るので
//   logout() してログイン画面に戻す。

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { apiRequest, refreshSession } from '../api/client';
import { onAuthExpired } from '../lib/auth-events';
import { getTokenSecondsRemaining } from '../lib/jwt';
import { readJson, STORAGE_KEYS, writeJson } from '../lib/storage';

export interface PerformerInfo {
  ulid: string;
  display_name: string;
  plan: 'free' | 'pro';
  status: 'pending' | 'active' | 'disabled';
}

interface LoginTokens {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  refresh_expires_in: number;
  performer: PerformerInfo;
}

interface SignupResult {
  performer_ulid: string;
  status: 'pending';
  message: string;
}

// 設計書 §3.4.1 + CLAUDE.md「Google OAuth 2要素フロー拡張」
// /v1/auth/google のレスポンスは Union 型(tokens / requires_link_confirmation)
export type GoogleLoginResult =
  | {
      kind: 'tokens';
      performer: PerformerInfo;
    }
  | {
      kind: 'requires_link_confirmation';
      link_token: string;
      link_expires_in: number;
      email: string;
    };

interface AuthContextValue {
  performer: PerformerInfo | null;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  loginWithGoogle: (idToken: string) => Promise<GoogleLoginResult>;
  confirmGoogleLink: (linkToken: string, password: string) => Promise<void>;
  signup: (email: string, password: string, displayName: string) => Promise<SignupResult>;
  logout: () => Promise<void>;
  updatePerformerLocal: (patch: Partial<PerformerInfo>) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function clearTokens(): void {
  localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
  localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
  localStorage.removeItem(STORAGE_KEYS.PERFORMER);
}

// access token の残り秒数を見て、次に refresh をかけるべき
// 「残り秒数」を計算する。
// - 残り > LEAD_TIME → (残り - LEAD_TIME) 後に refresh
// - 残り <= LEAD_TIME → 即座に refresh(0)
// - exp が読めない → null(タイマー張らない)
const REFRESH_LEAD_SECONDS = 120; // 残り 2 分で refresh
// setTimeout の引数上限(ms)。これを超えると 1 回目で即発火してしまうため、
// access token TTL が極端に長い場合に備えて分割する。
const MAX_TIMEOUT_MS = 2 ** 31 - 1;
// heartbeat refresh が一時的に失敗した場合のリトライ間隔(ネットワーク blip 対策)。
const HEARTBEAT_RETRY_AFTER_MS = 30_000;

function computeRefreshDelayMs(token: string | null): number | null {
  const remaining = getTokenSecondsRemaining(token);
  if (remaining === null) return null;
  const delaySec = Math.max(0, remaining - REFRESH_LEAD_SECONDS);
  return Math.min(delaySec * 1000, MAX_TIMEOUT_MS);
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [performer, setPerformer] = useState<PerformerInfo | null>(() =>
    readJson<PerformerInfo>(STORAGE_KEYS.PERFORMER),
  );
  const navigate = useNavigate();
  const heartbeatTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // refresh と heartbeat の再評価を 1 つにまとめるためのトリガ。
  // logout / login / token 更新時に bump される。
  const [authTick, setAuthTick] = useState(0);
  const bumpAuthTick = useCallback(() => setAuthTick((n) => n + 1), []);

  const stopHeartbeat = useCallback(() => {
    if (heartbeatTimerRef.current !== null) {
      clearTimeout(heartbeatTimerRef.current);
      heartbeatTimerRef.current = null;
    }
  }, []);

  const login = useCallback(
    async (email: string, password: string) => {
      const data = await apiRequest<LoginTokens>('/auth/login', {
        method: 'POST',
        body: { email, password },
      });
      localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, data.access_token);
      localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, data.refresh_token);
      writeJson(STORAGE_KEYS.PERFORMER, data.performer);
      setPerformer(data.performer);
      bumpAuthTick();
    },
    [bumpAuthTick],
  );

  // Google ID Token を API に送って 2要素フローを実行する。
  // - tokens 返却時はそのまま localStorage に保存しダッシュボードへ
  // - requires_link_confirmation 返却時はそのまま返し、UI 側でパスワード入力を求める
  // 設計書 §3.4.1 + CLAUDE.md「Google OAuth 2要素フロー拡張」
  const loginWithGoogle = useCallback(
    async (idToken: string): Promise<GoogleLoginResult> => {
      type ApiVariant =
        | (LoginTokens & { kind: 'tokens' })
        | {
            kind: 'requires_link_confirmation';
            link_token: string;
            link_expires_in: number;
            email: string;
          };
      const data = await apiRequest<ApiVariant>('/auth/google', {
        method: 'POST',
        body: { id_token: idToken },
      });
      if (data.kind === 'tokens') {
        localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, data.access_token);
        localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, data.refresh_token);
        writeJson(STORAGE_KEYS.PERFORMER, data.performer);
        setPerformer(data.performer);
        bumpAuthTick();
        return { kind: 'tokens', performer: data.performer };
      }
      return {
        kind: 'requires_link_confirmation',
        link_token: data.link_token,
        link_expires_in: data.link_expires_in,
        email: data.email,
      };
    },
    [bumpAuthTick],
  );

  // 2要素フローの第2ステップ。link_token + パスワードを送って連携を確定し、
  // 通常の access/refresh トークンを受け取って保存する。
  const confirmGoogleLink = useCallback(
    async (linkToken: string, password: string) => {
      const data = await apiRequest<LoginTokens & { kind: 'tokens' }>(
        '/auth/google/confirm-link',
        {
          method: 'POST',
          body: { link_token: linkToken, password },
        },
      );
      localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, data.access_token);
      localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, data.refresh_token);
      writeJson(STORAGE_KEYS.PERFORMER, data.performer);
      setPerformer(data.performer);
      bumpAuthTick();
    },
    [bumpAuthTick],
  );

  const signup = useCallback(
    async (email: string, password: string, displayName: string) => {
      return await apiRequest<SignupResult>('/auth/signup', {
        method: 'POST',
        body: { email, password, display_name: displayName },
      });
    },
    [],
  );

  const updatePerformerLocal = useCallback((patch: Partial<PerformerInfo>) => {
    setPerformer((prev) => {
      if (!prev) return prev;
      const next = { ...prev, ...patch };
      writeJson(STORAGE_KEYS.PERFORMER, next);
      return next;
    });
  }, []);

  const logout = useCallback(async () => {
    const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
    if (refreshToken) {
      try {
        await apiRequest('/auth/logout', {
          method: 'POST',
          body: { refresh_token: refreshToken },
        });
      } catch {
        // logout は best-effort、トークンが無効でもクライアント側は消す
      }
    }
    clearTokens();
    setPerformer(null);
    stopHeartbeat();
    bumpAuthTick();
  }, [bumpAuthTick, stopHeartbeat]);

  // セッション期限切れを apiRequest 側から受け取った時のハンドラ。
  // localStorage は client.ts 側で既にクリア済みなので、React state だけ
  // 整合させて /login に飛ばす。サーバーへの logout 呼び出しは呼ばない
  // (refresh が無効な状態で叩いても通らない)。
  useEffect(() => {
    const off = onAuthExpired('performer', () => {
      stopHeartbeat();
      setPerformer(null);
      bumpAuthTick();
      // リダイレクトは React Router の navigate を使って SPA 内で済ませる。
      // 既に /login の場合は何もしない(無限ループ防止)。
      if (
        typeof window !== 'undefined' &&
        !window.location.pathname.startsWith('/login') &&
        !window.location.pathname.startsWith('/signup')
      ) {
        navigate('/login', { replace: true });
      }
    });
    return off;
  }, [bumpAuthTick, navigate, stopHeartbeat]);

  // Heartbeat: access token の残り 2 分の時点で proactive refresh をかける。
  // - access token が無い: 何もしない
  // - exp が読めない: 即 refresh を試みる
  // - 既に期限切れ近く: 即 refresh(setTimeout 0)
  // refresh 結果に応じて自分自身を再スケジュール(authTick で発火)。
  // refresh が一時的に失敗(ネットワーク blip 等)した場合も再評価できるよう
  // 必ず bumpAuthTick する。refresh token 自体が無効な場合は apiRequest 側の
  // expireSession 通知 → onAuthExpired listener で performer state がクリア
  // され、次回 effect 実行時に early return する。
  useEffect(() => {
    if (!performer) {
      stopHeartbeat();
      return;
    }
    const accessToken = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
    const delay = computeRefreshDelayMs(accessToken);
    if (delay === null) {
      // exp が読めない token に当たった場合は API 駆動で expire が来るのを待つ
      // (ループ防止のためここでは bumpAuthTick しない)。
      void refreshSession('performer').then((newAccess) => {
        if (newAccess) bumpAuthTick();
      });
      return;
    }
    stopHeartbeat();
    heartbeatTimerRef.current = setTimeout(() => {
      heartbeatTimerRef.current = null;
      // 成功したら新 token の TTL でタイマーを張り直すため bump。
      // 失敗時は HEARTBEAT_RETRY_AFTER_MS 後に再試行する(ネットワーク blip 対応)。
      void refreshSession('performer').then((newAccess) => {
        if (newAccess) {
          bumpAuthTick();
        } else if (localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN)) {
          // expireSession で localStorage がクリア済みでない = 一時的失敗
          heartbeatTimerRef.current = setTimeout(() => {
            heartbeatTimerRef.current = null;
            bumpAuthTick();
          }, HEARTBEAT_RETRY_AFTER_MS);
        }
      });
    }, delay);
    return stopHeartbeat;
  }, [performer, authTick, bumpAuthTick, stopHeartbeat]);

  // visibilitychange でフォアグラウンドに戻った時の即時再評価。
  // ブラウザは非アクティブ時に setTimeout を秒〜分単位までスロットルすることが
  // あるため、戻り次第 token の残り時間を再計算する。
  useEffect(() => {
    function onVisibility() {
      if (typeof document === 'undefined') return;
      if (document.visibilityState !== 'visible') return;
      // performer state がまだあるかは bumpAuthTick → effect 側で判定する
      bumpAuthTick();
    }
    if (typeof document === 'undefined') return;
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [bumpAuthTick]);

  return (
    <AuthContext.Provider
      value={{
        performer,
        isAuthenticated: performer !== null,
        login,
        loginWithGoogle,
        confirmGoogleLink,
        signup,
        logout,
        updatePerformerLocal,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used inside <AuthProvider>');
  }
  return ctx;
}
